import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { toPortalFailure, type PortalFailure } from "../lib/errors";
import { refuseInView, sourcedKey, useReadSource, viewRows } from "./readSource";

/**
 * The read and write paths for Ärenden.
 *
 * Same shape as `portalQueries.ts`: every read is an ordinary RLS-scoped select, every write is a
 * named RPC, because `authenticated` holds no insert, update or delete grant on any request table.
 * A write that is not an RPC here would simply be refused, which the attack suite proves by calling
 * the tables directly.
 *
 * The list comes from `public.v_client_requests` rather than from `public.requests`. The view is the
 * client-visible projection: it carries `scope_result` and the latest waiting-for-client note, and
 * it has no column for estimated minutes because those live in `private.request_work_records` in a
 * schema this role has no USAGE on. `tests/db/request-read-model.test.ts` asserts that from the
 * catalogue rather than from this file.
 */

export const requestsKey = (tenantId: string) => ["portal", "requests", tenantId] as const;
/**
 * Build round 54, 2026-10-31: every request's thread at once. The client's `requests` channel says
 * that some request changed and not which one is open, so it refreshes them all by this prefix.
 */
export const requestThreadsPrefix = ["portal", "request-thread"] as const;
export const requestThreadKey = (requestId: string) =>
  [...requestThreadsPrefix, requestId] as const;
export const servicesKey = (tenantId: string) => ["portal", "services", tenantId] as const;
/** Build round A2, 2026-10-08: "Små ändringar den här månaden", one client's count and cap. */
export const requestMonthUsageKey = (tenantId: string) =>
  ["portal", "request-month-usage", tenantId] as const;

/**
 * The five statuses plan.md section 5.3 fixes, in the order a request moves through them, and the
 * sixth, `declined`.
 *
 * Build round A1, 2026-10-08: `declined` ("Avböjt") is final, like `completed`, and set only by
 * `public.admin_decline_request` with a reason the client reads. Nothing moves a declined request
 * again, and the client's reopen only ever reopened a completed one. Every place that sorts open from
 * closed asks `isClosedStatus` rather than comparing with `completed`, so the two final statuses
 * cannot drift apart between screens.
 */
export const REQUEST_STATUSES = [
  "received",
  "planned",
  "in_progress",
  "waiting_for_client",
  "completed",
  "declined"
] as const;

export type RequestStatus = (typeof REQUEST_STATUSES)[number];

/** The two statuses nothing moves on from, except a client's reopening of a completed request. */
export const CLOSED_STATUSES: ReadonlySet<string> = new Set<string>(["completed", "declined"]);

/** Whether a request is closed: Klart or Avböjt. Server side, Inkorg and the register agree. */
export function isClosedStatus(status: string): boolean {
  return CLOSED_STATUSES.has(status);
}

export const STATUS_LABEL: Record<RequestStatus, string> = {
  received: "Mottaget",
  planned: "Planerat",
  in_progress: "Pågår",
  waiting_for_client: "Väntar på er",
  completed: "Klart",
  declined: "Avböjt"
};

/**
 * plan.md section 5.3. The portal displays the classification; it does not decide it.
 *
 * `extra` since build round A1, 2026-10-08: the fourth answer to who pays and whether it counts.
 * Petrovic does the work for free beyond the monthly cap, and it never counts against it.
 */
export const SCOPE_LABEL: Record<string, string> = {
  included_in_management: "Ingår i förvaltning",
  free_defect: "Kostnadsfritt fel",
  separate_price_required: "Kräver separat pris",
  extra: "Extra"
};

/**
 * The five types a client may file. `public.create_request` keeps the same list and refuses
 * `improvement` by name, so the client's Nytt ärende offers exactly these.
 */
export const REQUEST_TYPES = ["change", "fault", "account", "incident", "other"] as const;

/**
 * Build round A1, 2026-10-08: the six types Petrovic may create through
 * `public.admin_create_request`, the client's five and `improvement` ("Förbättring"), which only
 * Petrovic creates. Kept apart from `REQUEST_TYPES` rather than filtered out of it, so the client's
 * picker cannot gain a type by someone extending the wrong list.
 */
export const ADMIN_REQUEST_TYPES = [...REQUEST_TYPES, "improvement"] as const;

export type AdminRequestType = (typeof ADMIN_REQUEST_TYPES)[number];

export const TYPE_LABEL: Record<string, string> = {
  change: "Ändring",
  fault: "Fel",
  account: "Konton och åtkomst",
  incident: "Incident",
  other: "Annat",
  improvement: "Förbättring"
};

/** `requests.origin`: who created the request. The client reads it since build round A1. */
export type RequestOrigin = "client" | "petrovic";

/**
 * Build round A2, 2026-10-08: Petrovic's three answers to a request over its month's cap,
 * `requests_over_cap_choice_check`. `extra` and `own_price` also set the classification (Extra,
 * Kräver separat pris), so the client reads those two there; `next_month` moves the month the request
 * counts in, which is the one the client is told about in its own words.
 */
export const OVER_CAP_CHOICES = ["extra", "next_month", "own_price"] as const;

export type OverCapChoice = (typeof OVER_CAP_CHOICES)[number];

/**
 * One line under each type in the client's picker, because "Fel" and "Incident" are hard to tell
 * apart from the names alone. The lines for Fel and Incident are Mattej's words, decided 2026-09-24
 * (docs/polish.md P-16); the other three are written in the same style.
 */
export const TYPE_HINT: Record<(typeof REQUEST_TYPES)[number], string> = {
  change: "Något ska läggas till, ändras eller tas bort.",
  fault: "Något fungerar inte som det ska.",
  account: "Någon hos er ska få eller mista åtkomst, eller ett konto ska ändras.",
  incident: "Sajten eller systemet ligger nere, eller något kan ha läckt.",
  other: "Allt som inte passar in ovan, till exempel en fråga."
};

export interface ClientRequest {
  id: string;
  tenant_id: string;
  service_id: string;
  request_number: number;
  title: string | null;
  request_type: string;
  status: RequestStatus;
  scope_result: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  waiting_for_client_note: string | null;
  /**
   * Build round A1, 2026-10-08. Who created the request: `client`, or `petrovic` through
   * `public.admin_create_request`. The request page says "Skapat av Petrovic" for the second.
   */
  origin: RequestOrigin;
  /** Why Petrovic declined the request, 1 to 500 characters of plain text. Null unless declined. */
  decline_reason: string | null;
  declined_at: string | null;
  /**
   * Build round A2, 2026-10-08. The calendar month, `YYYY-MM-01` in Europe/Stockholm, the request
   * counts toward the monthly cap in: the month it was received, or the next one when Petrovic chose
   * "Nästa månad". A date, read with `app/lib/requestMonth.ts` and never through `new Date`.
   */
  counts_in_month: string | null;
  /**
   * "Över månadens tak": created when the month's small changes were already used, and still waiting
   * for Petrovic's choice. The view writes it as that waiting mark, so it is false again once he has
   * chosen, classified or declined the request.
   */
  over_cap: boolean;
  /** Petrovic's choice for a request that was over the cap, or null. */
  over_cap_choice: OverCapChoice | null;
  /** The request this one was split from with "Dela upp ärendet", or null. */
  split_from_request_id: string | null;
}

/**
 * The month's small changes and the cap, for one client and the current calendar month.
 *
 * `cap` is null when no management covers the month, and then the count is not shown at all: a
 * client without förvaltning has no small changes to count.
 */
export interface RequestMonthUsage {
  /** `YYYY-MM-01`, Europe/Stockholm. */
  month: string;
  counted: number;
  cap: number | null;
}

export function useRequests(tenantId: string | null): UseQueryResult<ClientRequest[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, requestsKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        return viewRows<ClientRequest>("admin_as_client_requests", { p_tenant_id: source.tenantId });
      }
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("v_client_requests")
        .select(
          "id, tenant_id, service_id, request_number, title, request_type, status, scope_result, created_at, updated_at, completed_at, waiting_for_client_note, origin, decline_reason, declined_at, counts_in_month, over_cap, over_cap_choice, split_from_request_id"
        )
        .order("updated_at", { ascending: false })
        .limit(200);

      if (error) {
        throw error;
      }
      return (data ?? []) as ClientRequest[];
    }
  });
}

/**
 * Build round A2, 2026-10-08: `public.client_request_month_usage`, "Små ändringar den här månaden:
 * 2 av 3" above the list.
 *
 * A count rather than a select of requests, because the count is decided by one security definer
 * that reads every request of the month (`private.request_month_usage`), and a client counting its
 * own rows would count differently the day a rule changes. The gateway refuses any tenant the caller
 * is not a member of. One row or none; none is read as "nothing to show".
 */
export function useRequestMonthUsage(tenantId: string | null): UseQueryResult<RequestMonthUsage | null> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, requestMonthUsageKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        const rows = await viewRows<RequestMonthUsage>("admin_as_client_request_month_usage", { p_tenant_id: source.tenantId });
        return rows[0] ?? null;
      }
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase.rpc("client_request_month_usage", {
        p_tenant_id: tenantId as string
      });

      if (error) {
        throw toPortalFailure(error);
      }
      return ((data ?? []) as unknown as RequestMonthUsage[])[0] ?? null;
    }
  });
}

export interface PortalService {
  id: string;
  display_name: string;
  /**
   * Build round 46, 2026-10-17: read so an archived service shows as ended rather than live, and
   * leaves the new-request picker. The server refuses a request on it either way
   * (`PORTAL_SERVICE_ARCHIVED`).
   */
  lifecycle_status: string;
  /** Set exactly when `lifecycle_status` is `archived` (`services_archived_consistency_check`). */
  archived_at: string | null;
}

/** The day an archived service ended, or null for one that still runs. */
export function serviceEndedAt(service: PortalService): string | null {
  return service.lifecycle_status === "archived" ? service.archived_at : null;
}

/** The services a new request can be about: every one but the archived. */
export function requestableServices(services: PortalService[]): PortalService[] {
  return services.filter((service) => service.lifecycle_status !== "archived");
}

export function useServices(tenantId: string | null): UseQueryResult<PortalService[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, servicesKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        return viewRows<PortalService>("admin_as_client_services", { p_tenant_id: source.tenantId });
      }
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("services")
        .select("id, display_name, lifecycle_status, archived_at")
        .order("display_name", { ascending: true });

      if (error) {
        throw error;
      }
      return (data ?? []) as PortalService[];
    }
  });
}

export interface RequestComment {
  id: string;
  body: string | null;
  author_user_id: string | null;
  created_at: string;
}

export interface RequestStatusEvent {
  id: string;
  from_status: string | null;
  to_status: string;
  event_kind: string;
  actor_user_id: string | null;
  waiting_for_client_note: string | null;
  occurred_at: string;
}

export interface RequestThread {
  description: string | null;
  comments: RequestComment[];
  events: RequestStatusEvent[];
}

/**
 * The detail a request row does not carry.
 *
 * `description` is read from `public.requests` rather than from the view, because the view is a
 * list projection. Three selects rather than one join: PostgREST would embed them, but the embed
 * would have to name the foreign key relationship and the three RLS policies would still be
 * evaluated separately, so nothing is saved except a round trip.
 */
export function useRequestThread(requestId: string | null): UseQueryResult<RequestThread> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, requestThreadKey(requestId ?? "none")),
    enabled: Boolean(requestId),
    queryFn: async () => {
      const supabase = getSupabaseBrowserClient();
      // `enabled` already gates this on a non-null id; the narrowing is for the type checker.
      const id = requestId as string;

      if (source.kind === "admin") {
        const args = { p_tenant_id: source.tenantId, p_request_id: id };
        const [row, comments, events] = await Promise.all([
          viewRows<{ description: string | null }>("admin_as_client_request_description", args),
          viewRows<RequestComment>("admin_as_client_request_comments", args),
          viewRows<RequestStatusEvent>("admin_as_client_request_events", args)
        ]);
        return { description: row[0]?.description ?? null, comments, events };
      }

      const [row, comments, events] = await Promise.all([
        supabase.from("requests").select("description").eq("id", id).maybeSingle(),
        supabase
          .from("request_comments")
          .select("id, body, author_user_id, created_at")
          .eq("request_id", id)
          .order("created_at", { ascending: true })
          .limit(500),
        supabase
          .from("request_status_events")
          .select(
            "id, from_status, to_status, event_kind, actor_user_id, waiting_for_client_note, occurred_at"
          )
          .eq("request_id", id)
          .order("occurred_at", { ascending: true })
          .limit(500)
      ]);

      for (const result of [row, comments, events]) {
        if (result.error) {
          throw result.error;
        }
      }

      return {
        description: (row.data as { description: string | null } | null)?.description ?? null,
        comments: (comments.data ?? []) as RequestComment[],
        events: (events.data ?? []) as RequestStatusEvent[]
      };
    }
  });
}

export interface CreateRequestInput {
  tenantId: string;
  serviceId: string;
  title: string;
  description: string;
  requestType: string;
  /** Retries reuse the key, so a resend cannot create a second request. */
  idempotencyKey: string;
}

export function useCreateRequest() {
  const source = useReadSource();
  const queryClient = useQueryClient();

  return useMutation<{ id: string; request_number: number }, PortalFailure, CreateRequestInput>({
    mutationFn: async (input) => {
      refuseInView(source);
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase.rpc("create_request", {
        p_tenant_id: input.tenantId,
        p_service_id: input.serviceId,
        p_title: input.title,
        p_description: input.description,
        p_request_type: input.requestType,
        p_idempotency_key: input.idempotencyKey
      });

      if (error) {
        throw toPortalFailure(error);
      }
      return data as unknown as { id: string; request_number: number };
    },
    onSuccess: (_result, variables) =>
      queryClient.invalidateQueries({ queryKey: requestsKey(variables.tenantId) })
  });
}

export interface AddRequestCommentInput {
  tenantId: string;
  requestId: string;
  body: string;
  idempotencyKey: string;
}

export function useAddRequestComment() {
  const source = useReadSource();
  const queryClient = useQueryClient();

  return useMutation<{ id: string }, PortalFailure, AddRequestCommentInput>({
    mutationFn: async (input) => {
      refuseInView(source);
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase.rpc("add_request_comment", {
        p_tenant_id: input.tenantId,
        p_request_id: input.requestId,
        p_body: input.body,
        p_idempotency_key: input.idempotencyKey
      });

      if (error) {
        throw toPortalFailure(error);
      }
      return data as unknown as { id: string };
    },
    onSuccess: (_result, variables) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: requestThreadKey(variables.requestId) }),
        queryClient.invalidateQueries({ queryKey: requestsKey(variables.tenantId) })
      ])
  });
}

export interface ReopenRequestInput {
  tenantId: string;
  requestId: string;
  body: string;
}

/**
 * Reopening appends. plan.md section 5.3: the audit history stays intact, so the gateway adds a
 * `reopened` status event rather than removing the `completed` one, and the timeline shows both.
 */
export function useReopenRequest() {
  const source = useReadSource();
  const queryClient = useQueryClient();

  return useMutation<{ id: string }, PortalFailure, ReopenRequestInput>({
    mutationFn: async (input) => {
      refuseInView(source);
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase.rpc("reopen_request", {
        p_tenant_id: input.tenantId,
        p_request_id: input.requestId,
        p_body: input.body
      });

      if (error) {
        throw toPortalFailure(error);
      }
      return data as unknown as { id: string };
    },
    onSuccess: (_result, variables) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: requestThreadKey(variables.requestId) }),
        queryClient.invalidateQueries({ queryKey: requestsKey(variables.tenantId) })
      ])
  });
}
